import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { getS3Url } from "../../utils/imageUtils";
import { CardSecondaryText, formatDate, formatNumber } from "./MediaKitComponents";
import { MediaKitInProgressChip } from "./MediaKitMetricsWindowCaption";
import { useTranslation } from "react-i18next";

export type MediaKitYoutubeCatalogVideo = {
  id: string;
  title: string;
  url: string | null;
  thumbnail: string | null;
  imageS3Key: string | null;
  publishedAt: string | null;
  views: number;
  interactions: number;
  engagementRate: number | null;
  /** Younger than the media kit days-after-publish window (latest value shown). */
  inProgress?: boolean;
};

export function catalogThumbSrc(item: {
  thumbnail: string | null;
  imageS3Key: string | null;
}): string | null {
  if (item.imageS3Key) return getS3Url(item.imageS3Key);
  if (item.thumbnail) return item.thumbnail;
  return null;
}

export const MediaKitYoutubeCatalogList: React.FC<{
  videos: MediaKitYoutubeCatalogVideo[];
  linkItems?: boolean;
}> = ({ videos, linkItems = true }) => {
  const { t } = useTranslation("mediaKit");
  return (
  <Stack spacing={2}>
    {videos.map((item) => {
      const thumb = catalogThumbSrc(item);
      const inner = (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            data-mediakit-pdf-thumb-cover=""
            sx={{
              width: 120,
              height: 68,
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "action.hover",
              flexShrink: 0,
            }}
          >
            {thumb ? (
              <Box
                component="img"
                src={thumb}
                alt=""
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : null}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.title || t("untitled")}
            </Typography>
            {item.publishedAt ? (
              <CardSecondaryText
                variant="caption"
                display="block"
                sx={{ lineHeight: 1.2, mt: 0.25 }}
              >
                {formatDate(item.publishedAt)}
              </CardSecondaryText>
            ) : null}
            <Stack
              direction="row"
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.25 }}
            >
              <CardSecondaryText
                variant="caption"
                sx={{ display: "inline-flex", alignItems: "center" }}
              >
                <VisibilityIcon sx={{ fontSize: 14, mr: 0.5 }} />
                {formatNumber(item.views)}
              </CardSecondaryText>
              <CardSecondaryText
                variant="caption"
                sx={{ display: "inline-flex", alignItems: "center" }}
              >
                <TouchAppIcon sx={{ fontSize: 14, mr: 0.5 }} />
                {formatNumber(item.interactions)}
              </CardSecondaryText>
              {item.engagementRate != null && (
                <CardSecondaryText
                  variant="caption"
                  sx={{ display: "inline-flex", alignItems: "center" }}
                >
                  <ThumbUpIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  {item.engagementRate.toFixed(2)}%
                </CardSecondaryText>
              )}
              {item.inProgress && <MediaKitInProgressChip />}
            </Stack>
          </Box>
        </Stack>
      );
      if (linkItems && item.url) {
        return (
          <Box
            key={item.id}
            component="a"
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "inherit",
              textDecoration: "none",
              "&:hover": { opacity: 0.85 },
            }}
          >
            {inner}
          </Box>
        );
      }
      return <Box key={item.id}>{inner}</Box>;
    })}
  </Stack>
  );
};
